import React, { useState } from 'react';
import {
    MdClose,
    MdSave,
    MdWarning,
    MdAdd,
    MdDescription,
    MdLabel,
    MdCheckCircle,
    MdCalendarToday,
    MdAccessTime,
    MdVisibility,
    MdSchedule
} from 'react-icons/md';
import { useClass } from '../utils/ClassContext';

const CreateEnvironmentModal = ({ isOpen, onClose, onSuccess, userRole, apiBaseUrl, username }) => {
    const { getClassContext } = useClass();
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        requirements: [],
        scheduling: {
            isVisible: false,
            openDate: '',
            closeDate: '',
            autoRelease: false
        }
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    // Reset form when modal opens/closes
    React.useEffect(() => {
        if (isOpen) {
            setFormData({
                name: '',
                description: '',
                requirements: [],
                scheduling: {
                    isVisible: false,
                    openDate: '',
                    closeDate: '',
                    autoRelease: false
                }
            });
            setError('');
            setSuccess(false);
        }
    }, [isOpen]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const addRequirement = () => {
        setFormData(prev => ({
            ...prev,
            requirements: [
                ...prev.requirements,
                {
                    id: Date.now(),
                    description: '',
                    questions: [''],
                    rubrics: [''],
                    visible: true
                }
            ]
        }));
    };

    const removeRequirement = (id) => {
        setFormData(prev => ({
            ...prev,
            requirements: prev.requirements.filter(req => req.id !== id)
        }));
    };

    const updateRequirement = (id, field, value) => {
        setFormData(prev => ({
            ...prev,
            requirements: prev.requirements.map(req =>
                req.id === id ? { ...req, [field]: value } : req
            )
        }));
    };

    const updateRequirementArray = (id, field, index, value) => {
        setFormData(prev => ({
            ...prev,
            requirements: prev.requirements.map(req =>
                req.id === id
                    ? {
                        ...req,
                        [field]: req[field].map((item, i) => i === index ? value : item)
                    }
                    : req
            )
        }));
    };

    const addRequirementArrayItem = (id, field) => {
        setFormData(prev => ({
            ...prev,
            requirements: prev.requirements.map(req =>
                req.id === id
                    ? { ...req, [field]: [...req[field], ''] }
                    : req
            )
        }));
    };

    const removeRequirementArrayItem = (id, field, index) => {
        setFormData(prev => ({
            ...prev,
            requirements: prev.requirements.map(req =>
                req.id === id
                    ? { ...req, [field]: req[field].filter((_, i) => i !== index) }
                    : req
            )
        }));
    };

    const validateForm = () => {
        if (!formData.name.trim()) {
            setError('Environment name is required');
            return false;
        }

        if (!formData.description.trim()) {
            setError('Environment description is required');
            return false;
        }

        // Validate requirements
        for (const req of formData.requirements) {
            if (!req.description.trim()) {
                setError('All requirements must have a description');
                return false;
            }

            if (req.questions.some(q => !q.trim())) {
                setError('All requirement questions must be filled');
                return false;
            }

            if (req.rubrics.some(r => !r.trim())) {
                setError('All requirement rubrics must be filled');
                return false;
            }
        }

        // Validate scheduling
        if (formData.scheduling.autoRelease) {
            if (!formData.scheduling.openDate) {
                setError('Open date is required when automatic scheduling is enabled');
                return false;
            }

            if (!formData.scheduling.closeDate) {
                setError('Close date is required when automatic scheduling is enabled');
                return false;
            }

            const openDate = new Date(formData.scheduling.openDate);
            const closeDate = new Date(formData.scheduling.closeDate);

            if (closeDate <= openDate) {
                setError('Close date must be after open date');
                return false;
            }
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        const classContext = getClassContext();
        if (!classContext || !classContext.class_code) {
            setError('Please select a class before creating an environment');
            return;
        }

        setLoading(true);
        setError('');

        try {
            // Prepare requirements data in the format expected by the backend
            const processedRequirements = formData.requirements.map(req => ({
                description: req.description,
                questions: req.questions.filter(q => q.trim()),
                rubrics: req.rubrics.filter(r => r.trim()),
                visible: req.visible
            }));

            const environmentData = {
                name: formData.name,
                description: formData.description,
                class_code: classContext.class_code,
                requirements: processedRequirements,
                grading_rubric: {}, // Can be extended later if needed
                scheduling: {
                    isVisible: formData.scheduling.isVisible,
                    openDate: formData.scheduling.openDate ? new Date(formData.scheduling.openDate).toISOString() : null,
                    closeDate: formData.scheduling.closeDate ? new Date(formData.scheduling.closeDate).toISOString() : null,
                    autoRelease: formData.scheduling.autoRelease
                }
            };

            const response = await fetch(`${apiBaseUrl}/environments`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'User-Role': userRole,
                    'Requesting-User': username,
                    'Class-Code': classContext.class_code
                },
                credentials: 'include',
                body: JSON.stringify(environmentData)
            });

            if (response.ok) {
                
                setSuccess(true);
                setTimeout(() => {
                    onSuccess();
                    onClose();
                }, 1500);
            } else {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to create environment');
            }
        } catch (err) {
            console.error('Error creating environment:', err);
            setError(err.message || 'Failed to create environment');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                {/* Background overlay */}
                <div
                    className="fixed inset-0 transition-opacity"
                    aria-hidden="true"
                    onClick={onClose}
                >
                    <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
                </div>

                {/* Modal */}
                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
                    {/* Header */}
                    <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center space-x-3">
                                <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-blue-500 rounded-full flex items-center justify-center">
                                    <MdAdd className="text-white text-xl" />
                                </div>
                                <h3 className="text-lg font-medium text-gray-900">
                                    Create New Environment
                                </h3>
                            </div>
                            <button
                                onClick={onClose}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <MdClose className="text-xl" />
                            </button>
                        </div>

                        {/* Success Message */}
                        {success && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                                <div className="flex items-center">
                                    <MdCheckCircle className="text-green-600 text-xl mr-3" />
                                    <p className="text-green-800">Environment created successfully!</p>
                                </div>
                            </div>
                        )}

                        {/* Error Message */}
                        {error && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                                <div className="flex items-center">
                                    <MdWarning className="text-red-600 text-xl mr-3" />
                                    <p className="text-red-800">{error}</p>
                                </div>
                            </div>
                        )}

                        {/* Form */}
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* Basic Information */}
                            <div className="grid md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        <MdLabel className="inline mr-1" />
                                        Environment Name *
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        placeholder="e.g., Hospital, Library, University"
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        <MdDescription className="inline mr-1" />
                                        Description *
                                    </label>
                                    <input
                                        type="text"
                                        name="description"
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        placeholder="Brief description of the environment"
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        required
                                    />
                                </div>
                            </div>

                            {/* Requirements Section */}
                            <div>
                                <div className="flex items-center justify-between mb-4">
                                    <h4 className="text-lg font-medium text-gray-900">Requirements</h4>
                                    <button
                                        type="button"
                                        onClick={addRequirement}
                                        className="px-3 py-1 bg-blue-100 text-blue-700 rounded-md text-sm font-medium hover:bg-blue-200 transition-colors"
                                    >
                                        <MdAdd className="inline mr-1" />
                                        Add Requirement
                                    </button>
                                </div>

                                {formData.requirements.length === 0 ? (
                                    <div className="text-center py-8 text-gray-500">
                                        <p>No requirements added yet. Click "Add Requirement" to get started.</p>
                                    </div>
                                ) : (
                                    <div className="space-y-4 max-h-96 overflow-y-auto">
                                        {formData.requirements.map((req, index) => (
                                            <div key={req.id} className="border border-gray-200 rounded-lg p-4">
                                                <div className="flex items-center justify-between mb-3">
                                                    <h5 className="font-medium text-gray-800">
                                                        Requirement {index + 1}
                                                    </h5>
                                                    <button
                                                        type="button"
                                                        onClick={() => removeRequirement(req.id)}
                                                        className="text-red-600 hover:text-red-800 text-sm"
                                                    >
                                                        Remove
                                                    </button>
                                                </div>

                                                {/* Requirement Description */}
                                                <div className="mb-3">
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Description *
                                                    </label>
                                                    <textarea
                                                        value={req.description}
                                                        onChange={(e) => updateRequirement(req.id, 'description', e.target.value)}
                                                        placeholder="Describe the requirement..."
                                                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                        rows="2"
                                                        required
                                                    />
                                                </div>

                                                {/* Questions */}
                                                <div className="mb-3">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <label className="block text-sm font-medium text-gray-700">
                                                            Questions
                                                        </label>
                                                        <button
                                                            type="button"
                                                            onClick={() => addRequirementArrayItem(req.id, 'questions')}
                                                            className="text-blue-600 hover:text-blue-800 text-sm"
                                                        >
                                                            + Add Question
                                                        </button>
                                                    </div>
                                                    {req.questions.map((question, qIndex) => (
                                                        <div key={qIndex} className="flex items-center space-x-2 mb-2">
                                                            <input
                                                                type="text"
                                                                value={question}
                                                                onChange={(e) => updateRequirementArray(req.id, 'questions', qIndex, e.target.value)}
                                                                placeholder="Enter question..."
                                                                className="flex-1 border border-gray-300 rounded-md px-3 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                            />
                                                            {req.questions.length > 1 && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => removeRequirementArrayItem(req.id, 'questions', qIndex)}
                                                                    className="text-red-600 hover:text-red-800 text-sm"
                                                                >
                                                                    Remove
                                                                </button>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>

                                                {/* Rubrics */}
                                                <div className="mb-3">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <label className="block text-sm font-medium text-gray-700">
                                                            Rubrics
                                                        </label>
                                                        <button
                                                            type="button"
                                                            onClick={() => addRequirementArrayItem(req.id, 'rubrics')}
                                                            className="text-blue-600 hover:text-blue-800 text-sm"
                                                        >
                                                            + Add Rubric
                                                        </button>
                                                    </div>
                                                    {req.rubrics.map((rubric, rIndex) => (
                                                        <div key={rIndex} className="flex items-center space-x-2 mb-2">
                                                            <input
                                                                type="text"
                                                                value={rubric}
                                                                onChange={(e) => updateRequirementArray(req.id, 'rubrics', rIndex, e.target.value)}
                                                                placeholder="Enter rubric..."
                                                                className="flex-1 border border-gray-300 rounded-md px-3 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                            />
                                                            {req.rubrics.length > 1 && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => removeRequirementArrayItem(req.id, 'rubrics', rIndex)}
                                                                    className="text-red-600 hover:text-red-800 text-sm"
                                                                >
                                                                    Remove
                                                                </button>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>

                                                {/* Visibility */}
                                                <div>
                                                    <label className="flex items-center space-x-2">
                                                        <input
                                                            type="checkbox"
                                                            checked={req.visible}
                                                            onChange={(e) => updateRequirement(req.id, 'visible', e.target.checked)}
                                                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                        />
                                                        <span className="text-sm text-gray-700">
                                                            Visible to students
                                                        </span>
                                                    </label>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Scheduling Section */}
                            <div className="border-t pt-6">
                                <h4 className="text-lg font-medium text-gray-900 mb-4">
                                    <MdSchedule className="inline mr-2" />
                                    Environment Visibility & Scheduling
                                </h4>
                                
                                <div className="grid md:grid-cols-2 gap-4">
                                    {/* Manual Visibility */}
                                    <div className="space-y-3">
                                        <h5 className="font-medium text-gray-800">Manual Control</h5>
                                        <label className="flex items-center space-x-2">
                                            <input
                                                type="checkbox"
                                                checked={formData.scheduling.isVisible}
                                                onChange={(e) => setFormData(prev => ({
                                                    ...prev,
                                                    scheduling: {
                                                        ...prev.scheduling,
                                                        isVisible: e.target.checked
                                                    }
                                                }))}
                                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                            />
                                            <span className="text-sm text-gray-700">
                                                <MdVisibility className="inline mr-1" />
                                                Make environment visible to students immediately
                                            </span>
                                        </label>
                                    </div>

                                    {/* Automatic Scheduling */}
                                    <div className="space-y-3">
                                        <h5 className="font-medium text-gray-800">Automatic Scheduling</h5>
                                        <label className="flex items-center space-x-2">
                                            <input
                                                type="checkbox"
                                                checked={formData.scheduling.autoRelease}
                                                onChange={(e) => setFormData(prev => ({
                                                    ...prev,
                                                    scheduling: {
                                                        ...prev.scheduling,
                                                        autoRelease: e.target.checked
                                                    }
                                                }))}
                                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                            />
                                            <span className="text-sm text-gray-700">
                                                Enable automatic release/hide based on dates
                                            </span>
                                        </label>
                                    </div>
                                </div>

                                {/* Date Inputs */}
                                {formData.scheduling.autoRelease && (
                                    <div className="grid md:grid-cols-2 gap-4 mt-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                <MdCalendarToday className="inline mr-1" />
                                                Open Date & Time
                                            </label>
                                            <input
                                                type="datetime-local"
                                                value={formData.scheduling.openDate}
                                                onChange={(e) => setFormData(prev => ({
                                                    ...prev,
                                                    scheduling: {
                                                        ...prev.scheduling,
                                                        openDate: e.target.value
                                                    }
                                                }))}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                <MdAccessTime className="inline mr-1" />
                                                Close Date & Time
                                            </label>
                                            <input
                                                type="datetime-local"
                                                value={formData.scheduling.closeDate}
                                                onChange={(e) => setFormData(prev => ({
                                                    ...prev,
                                                    scheduling: {
                                                        ...prev.scheduling,
                                                        closeDate: e.target.value
                                                    }
                                                }))}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </form>
                    </div>

                    {/* Footer */}
                    <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                        <button
                            onClick={handleSubmit}
                            disabled={loading || success}
                            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:bg-blue-400"
                        >
                            {loading ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                    Creating...
                                </>
                            ) : (
                                <>
                                    <MdSave className="mr-1" />
                                    Create Environment
                                </>
                            )}
                        </button>
                        <button
                            onClick={onClose}
                            disabled={loading}
                            className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CreateEnvironmentModal;